import { useCallback, useEffect, useMemo, useState, useTransition, type ReactNode } from "react";
import { Alert, Button, Card, Chip, PageContainer, RuntimeDiagnosticBanner, type ComponentDiagnostic } from "@swot/base-components";
import { createIdempotencyKey, normalizeApiError, type NormalizedApiError } from "../../services/api.service.js";
import { useCmtsSelector } from "../../store/hooks.js";
import { selectAuthState, selectEffectivePermissions, selectRoles } from "../../store/selectors.js";
import { useCmtsDebouncedSearch } from "../../hooks/useCmtsDebouncedSearch.js";
import { CmtsApiErrorPanel } from "../CmtsApiErrorPanel.js";
import { CmtsDeleteDeactivateDialog } from "../CmtsDeleteDeactivateDialog.js";
import { CmtsEnterpriseDataGrid } from "../CmtsEnterpriseDataGrid.js";
import { CmtsEnterpriseModal } from "../CmtsEnterpriseModal.js";
import { CmtsPermissionActionBar } from "../CmtsPermissionActionBar.js";
import type { CmtsGridRowAction } from "../data-grid/index.js";
import { cmtsMasterDataCrudConfigFor, type CmtsMasterDataCrudEntityConfig, type CmtsMasterDataCrudEntityKey } from "../../core/master-data/cmtsMasterDataCrudRegistry.js";
import { isCmtsGuid } from "../../core/utils/cmtsGuid.js";
import type { DependencyCheckResult, DropdownOption, EntityRecord, EntitySchema, PageQuery, PagedResult, ValidationFieldError, ValidationResult } from "../../types/api.types.js";
import type { CmtsPermissionSet } from "../../types/pageConfig.types.js";
import { CmtsMasterDataDetailPanel } from "./CmtsMasterDataDetailPanel.js";
import { CmtsMasterDataDependencyPanel } from "./CmtsMasterDataDependencyPanel.js";
import { CmtsMasterDataForm, type CmtsMasterDataDropdownMap } from "./CmtsMasterDataForm.js";
import { CmtsMasterDataStatusBadge, isCmtsMasterDataRecordActive } from "./CmtsMasterDataStatusBadge.js";
import { CmtsMasterDataToolbar, type CmtsMasterDataToolbarState } from "./CmtsMasterDataToolbar.js";

export type CmtsMasterDataCrudShellProps = {
  readonly entityKey: CmtsMasterDataCrudEntityKey;
  readonly diagnostics?: readonly ComponentDiagnostic[] | undefined;
  readonly effectivePermissions?: readonly string[] | undefined;
};

type ModalState =
  | { readonly mode: "none" }
  | { readonly mode: "create" }
  | { readonly mode: "edit"; readonly record: EntityRecord }
  | { readonly mode: "detail"; readonly record: EntityRecord };

type DestructiveAction = "deactivate" | "activate" | "delete";

type PendingDestructiveAction = {
  readonly action: DestructiveAction;
  readonly record: EntityRecord;
  readonly dependencyCheck?: DependencyCheckResult | undefined;
};

type ShellError = {
  readonly title: string;
  readonly message: string;
  readonly normalized?: NormalizedApiError | undefined;
  readonly fieldErrors?: readonly ValidationFieldError[] | undefined;
  readonly dependencyCheck?: DependencyCheckResult | undefined;
};

const EMPTY_PAGED_RESULT: PagedResult<EntityRecord> = { items: [], page: 1, pageSize: 25, totalCount: 0 };

function permissionSetOf(config: CmtsMasterDataCrudEntityConfig): CmtsPermissionSet {
  return {
    read: config.permission.view,
    create: config.permission.create,
    update: config.permission.edit,
    activate: config.permission.activate,
    deactivate: config.permission.deactivate,
    delete: config.permission.delete,
    export: config.permission.view,
  };
}

function hasPermission(required: string, effectivePermissions: readonly string[] | undefined): boolean {
  const granted = new Set((effectivePermissions ?? []).map((item) => item.trim().toUpperCase()).filter(Boolean));
  if (granted.has("PLATFORM_ADMIN.MANAGE") || granted.has("SYSTEM_CONFIG.ADMIN_CONFIGURE") || granted.has("MASTER_DATA.MANAGE") || granted.has("AUTH.USER_MANAGE") || granted.has("PLATFORM_ADMIN.READ")) return true;
  return granted.has(required.trim().toUpperCase());
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function readRecordString(record: EntityRecord, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function formatDisplayDate(value: unknown): string {
  if (!value) return "-";
  try {
    const d = new Date(String(value));
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-GB") + ", " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
  } catch {
    // fallback
  }
  return String(value);
}

const SUBCATEGORY_TO_CATEGORY_MAP: Record<string, string> = {
  DIAMETER_OVERSIZE: "Dimensional Quality",
  DIAMETER_UNDERSIZE: "Dimensional Quality",
  HOLE_POSITION_ERROR: "Dimensional Quality",
  FLATNESS_OUT: "Dimensional Quality",
  RUNOUT_HIGH: "Dimensional Quality",
  THREAD_GAUGE_FAIL: "Dimensional Quality",
  SCRATCH_DENT: "Surface Finish / Coating",
  ROUGHNESS_HIGH: "Surface Finish / Coating",
  PAINT_PEEL: "Surface Finish / Coating",
  PLATING_THICKNESS_LOW: "Surface Finish / Coating",
  CORROSION_RUST: "Surface Finish / Coating",
  WRONG_MATERIAL_GRADE: "Material / Metallurgy",
  CHEMISTRY_OUT: "Material / Metallurgy",
  HARDNESS_LOW: "Material / Metallurgy",
  HARDNESS_HIGH: "Material / Metallurgy",
  CRACK_INCLUSION: "Material / Metallurgy",
  TORQUE_LOW: "Assembly Fitment",
  TORQUE_HIGH: "Assembly Fitment",
  MISSING_COMPONENT: "Assembly Fitment",
  WRONG_COMPONENT: "Assembly Fitment",
  FITMENT_INTERFERENCE: "Assembly Fitment",
  OPEN_CIRCUIT: "Electrical / Functional",
  SHORT_CIRCUIT: "Electrical / Functional",
  INSULATION_FAIL: "Electrical / Functional",
  WRONG_WIRING: "Electrical / Functional",
  MISSING_COC: "Documentation / Traceability",
  REVISION_MISMATCH: "Documentation / Traceability",
  BARCODE_SCAN_FAIL: "Documentation / Traceability",
  WRONG_LABEL: "Documentation / Traceability",
  PACKAGING_DAMAGE: "Packaging / Logistics",
  LABEL_MISSING: "Packaging / Logistics",
  TRANSIT_RUST: "Packaging / Logistics",
  MIXED_PARTS: "Packaging / Logistics",
  LINE_STOP: "Customer Line Stop",
  FIELD_FAILURE: "Customer Line Stop",
  SAFETY_INCIDENT: "Safety / EHS",
  HAZARD_CONDITION: "Safety / EHS",
};

const CATEGORY_CODE_TO_NAME: Record<string, string> = {
  DIMENSIONAL_QUALITY: "Dimensional Quality",
  SURFACE_FINISH_COATING: "Surface Finish / Coating",
  MATERIAL_METALLURGY: "Material / Metallurgy",
  ASSEMBLY_FITMENT: "Assembly Fitment",
  ELECTRICAL_FUNCTIONAL: "Electrical / Functional",
  DOCUMENTATION_TRACEABILITY: "Documentation / Traceability",
  PACKAGING_LOGISTICS: "Packaging / Logistics",
  CUSTOMER_LINE_STOP: "Customer Line Stop",
  SAFETY_EHS: "Safety / EHS",
  SUPPLIER_QUALITY: "Supplier Quality",
  SYSTEM_WORKFLOW: "System / Workflow",
  COMMERCIAL_BILLING: "Commercial / Billing",
};

function normalizeRecord(row: EntityRecord, config: CmtsMasterDataCrudEntityConfig, index: number, dropdowns?: CmtsMasterDataDropdownMap): EntityRecord {
  const rawId = readRecordString(row, ["id", "Id", "categoryId", "CategoryId", "recordId", "RecordId", "entityId", "EntityId", "key"]);
  const backendId = rawId && isCmtsGuid(rawId) ? rawId : (rawId || undefined);
  const rowKey = backendId ?? `${config.entityKey}-display-row-${index + 1}`;
  const code = readRecordString(row, ["code", "Code", "key", "value"]) ?? backendId ?? rowKey;
  const label = readRecordString(row, ["label", "Label", "name", "Name", "displayName", "title", "value", "code"]) ?? code;
  const active = isCmtsMasterDataRecordActive(row);
  const description = readRecordString(row, ["description", "Description", "desc", "remarks", "details", "comment"]);
  const metadata = (typeof row.metadata === "object" && row.metadata !== null ? row.metadata : {}) as Record<string, unknown>;
  const rawSortOrder = row.sortOrder ?? row.SortOrder ?? row.order ?? row.sequence ?? metadata.sortOrder;
  const sortOrder = rawSortOrder !== undefined && rawSortOrder !== null && Number.isFinite(Number(rawSortOrder)) && Number(rawSortOrder) > 0
    ? Number(rawSortOrder)
    : index + 1;
  const rawUsage = row.usageCount ?? row.UsageCount ?? row.usedByCount ?? row.usage ?? row.complaintCount ?? row.count;
  const usageCount = rawUsage !== undefined && rawUsage !== null ? rawUsage : "0";
  const rawUpdated = readRecordString(row, [
    "updatedAt", "UpdatedAt", "updatedAtUtc", "modifiedAtUtc", "modifiedAt",
    "updatedDate", "createdDate", "createdAt", "createdAtUtc"
  ]) ?? (typeof metadata.updatedAt === "string" ? metadata.updatedAt : undefined);
  const updatedFormatted = rawUpdated && rawUpdated !== "0001-01-01T00:00:00+00:00" ? formatDisplayDate(rawUpdated) : "-";

  const normalized: Record<string, unknown> = {
    ...row,
    id: backendId,
    __rowKey: rowKey,
    code,
    label,
    name: row.name ?? row.Name ?? label,
    description: description ?? (typeof row.description === "string" && row.description ? row.description : "-"),
    masterType: row.masterType ?? config.masterType,
    status: row.status ?? (active ? "Active" : "Inactive"),
    active,
    isActive: row.isActive ?? row.IsActive ?? active,
    sortOrder,
    sort: sortOrder,
    usageCount,
    usage: usageCount,
    updatedAt: updatedFormatted,
    updated: updatedFormatted,
  };
  if (config.entityKey === "complaint-subcategory") {
    let resolvedCategory: string | undefined;

    // 1. Direct label or name in row
    const rawCategoryLabel = readRecordString(row, ["parentCategoryLabel", "parentCategoryName", "categoryLabel", "categoryName", "parentLabel", "parentName"]);
    if (rawCategoryLabel && rawCategoryLabel !== "-" && !isCmtsGuid(rawCategoryLabel)) {
      resolvedCategory = rawCategoryLabel;
    }

    // 2. Direct code in row or metadata
    if (!resolvedCategory) {
      const rawCode = String(row.parentCategoryCode ?? row.parentCode ?? row.categoryCode ?? metadata.parentCategoryCode ?? metadata.parentCode ?? "").trim().toUpperCase();
      if (rawCode && rawCode !== "-" && !isCmtsGuid(rawCode)) {
        resolvedCategory = CATEGORY_CODE_TO_NAME[rawCode] ?? rawCode;
      }
    }

    // 3. Dropdown options lookup by ID or Code
    if (!resolvedCategory && dropdowns) {
      const categoryOptions = dropdowns["complaint-category"] ?? [];
      const parentId = readRecordString(row, ["parentCategoryId", "parentId", "categoryId"]) ?? readRecordString(metadata as EntityRecord, ["parentCategoryId", "parentId", "categoryId"]);
      if (parentId) {
        const found = categoryOptions.find((o) => o.value === parentId || o.code === parentId);
        if (found) {
          resolvedCategory = found.label || CATEGORY_CODE_TO_NAME[String(found.code ?? "").toUpperCase()] || found.code;
        }
      }
    }

    // 4. Known subcategory mapping
    if (!resolvedCategory) {
      const subCode = String(code).trim().toUpperCase();
      if (SUBCATEGORY_TO_CATEGORY_MAP[subCode]) {
        resolvedCategory = SUBCATEGORY_TO_CATEGORY_MAP[subCode];
      } else {
        for (const [key, cat] of Object.entries(SUBCATEGORY_TO_CATEGORY_MAP)) {
          if (subCode.includes(key) || key.includes(subCode)) {
            resolvedCategory = cat;
            break;
          }
        }
      }
    }

    normalized.parentCategoryLabel = resolvedCategory ?? "-";
    normalized.parent = normalized.parentCategoryLabel;
  }
  if (config.entityKey === "reference-data") {
    const referenceType = row.referenceTypeLabel ??
      row.typeLabel ??
      row.referenceTypeName ??
      row.referenceTypeCode ??
      row.referenceTypeId ??
      (typeof metadata.group === "string" ? metadata.group : undefined) ??
      (typeof metadata.referenceTypeCode === "string" ? metadata.referenceTypeCode : undefined);
    normalized.referenceTypeLabel = referenceType && referenceType !== "-" ? referenceType : "-";
    normalized.parent = normalized.referenceTypeLabel;
  }
  if (config.entityKey === "complaint-category") {
    const parent = row.parentCategoryCode ?? row.parentCode ?? row.parentCategoryName ?? row.parentCategoryId;
    normalized.parentCategoryCode = parent && parent !== "-" ? parent : "Root (Level 1)";
    normalized.parent = normalized.parentCategoryCode;
  }
  return normalized;
}

function activeOnlyFromFilter(filter: CmtsMasterDataToolbarState["activeFilter"]): boolean | undefined {
  if (filter === "active") return true;
  if (filter === "inactive") return false;
  return undefined;
}

function createListQuery(state: CmtsMasterDataToolbarState, debouncedSearch: string, config: CmtsMasterDataCrudEntityConfig): PageQuery {
  const filters: Record<string, string | number | boolean | null | undefined> = {};
  if (config.parent?.fieldName && config.entityKey === "complaint-subcategory") filters.parentField = config.parent.fieldName;
  if (config.parent?.fieldName && config.entityKey === "reference-data") filters.parentField = config.parent.fieldName;
  const query: PageQuery = {
    page: state.page,
    pageSize: state.pageSize,
    search: debouncedSearch,
    sortBy: state.sortBy,
    sortDirection: state.sortDirection,
    activeOnly: activeOnlyFromFilter(state.activeFilter),
    filters,
  };
  return query;
}

function fallbackSchema(config: CmtsMasterDataCrudEntityConfig): EntitySchema {
  return {
    entityType: config.masterType,
    version: "static-fallback:backend-schema-unavailable",
    fields: config.fallbackFields,
  };
}

function normalizeCode(value: unknown): string {
  return String(value ?? "").trim().replace(/\s+/g, "_").replace(/[^A-Za-z0-9_\-.]/g, "").toUpperCase();
}

function normalizePayload(payload: Record<string, unknown>, config: CmtsMasterDataCrudEntityConfig, selected?: EntityRecord | null): Record<string, unknown> {
  const label = String(payload.label ?? payload.name ?? selected?.label ?? selected?.name ?? "").trim();
  const code = normalizeCode(payload.code ?? selected?.code);
  const description = String(payload.description ?? payload.remarks ?? selected?.description ?? "").trim();
  const isActive = typeof payload.isActive === "boolean" ? payload.isActive : typeof payload.active === "boolean" ? payload.active : selected?.active ?? true;

  const normalized: Record<string, unknown> = {
    ...payload,
    code,
    name: payload.name ?? label,
    label,
    description: description || undefined,
    isActive,
    active: isActive,
    status: isActive ? "Active" : "Inactive",
  };

  if (payload.tenantId ?? selected?.tenantId) {
    normalized.tenantId = payload.tenantId ?? selected?.tenantId;
  }

  if (payload.sortOrder !== undefined && payload.sortOrder !== "") {
    const sortOrder = Number(payload.sortOrder);
    normalized.sortOrder = Number.isFinite(sortOrder) ? sortOrder : 0;
  }

  if (payload.parentCategoryId !== undefined) {
    normalized.parentCategoryId = payload.parentCategoryId;
    normalized.parentId = payload.parentCategoryId;
  }
  if (payload.parentId !== undefined) {
    normalized.parentId = payload.parentId;
    normalized.parentCategoryId = payload.parentId;
  }
  if (config.parent && payload[config.parent.fieldName]) {
    normalized[config.parent.fieldName] = payload[config.parent.fieldName];
    normalized.parentId = payload[config.parent.fieldName];
  }

  return normalized;
}

function sameBusinessKey(record: EntityRecord, payload: Record<string, unknown>, config: CmtsMasterDataCrudEntityConfig, selectedId: string | undefined): boolean {
  const recordId = String(record.id ?? "");
  if (selectedId && recordId === selectedId) return false;
  return config.businessKey.every((field) => {
    if (field === "tenantId") return true;
    if (field === "masterType") return String(record.masterType ?? config.masterType) === String(payload.masterType ?? config.masterType);
    const left = String(record[field] ?? "").trim().toLowerCase();
    const right = String(payload[field] ?? "").trim().toLowerCase();
    return left !== "" && right !== "" && left === right;
  });
}

function findOptionByValue(options: readonly DropdownOption[] | undefined, value: unknown): DropdownOption | undefined {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;
  return (options ?? []).find((option) => option.value === raw || option.code === raw || option.label === raw);
}

function backendGuidFromRecord(record: EntityRecord): string | undefined {
  const directId = readRecordString(record, ["id", "Id", "categoryId", "CategoryId", "recordId", "RecordId", "entityId", "EntityId", "key"]);
  if (directId && isCmtsGuid(directId)) return directId;
  for (const [key, val] of Object.entries(record)) {
    if (typeof val === "string" && isCmtsGuid(val) && (key.toLowerCase().includes("id") || key === "key")) {
      return val;
    }
  }
  return directId || undefined;
}

function validateClientPayload(input: {
  readonly payload: Record<string, unknown>;
  readonly config: CmtsMasterDataCrudEntityConfig;
  readonly currentRows: readonly EntityRecord[];
  readonly selected?: EntityRecord | null | undefined;
  readonly dropdowns: CmtsMasterDataDropdownMap;
}): ValidationResult {
  const errors: ValidationFieldError[] = [];
  const payload = input.payload;
  const code = String(payload.code ?? "").trim();
  const label = String(payload.label ?? payload.name ?? "").trim();
  if (!code) errors.push({ field: "code", message: "Code is required.", code: "CMTS_ERR_MISSING_CODE" });
  if (!label) errors.push({ field: "label", message: "Label is required.", code: "CMTS_ERR_MISSING_LABEL" });
  if (payload.sortOrder !== undefined && payload.sortOrder !== "" && !Number.isFinite(Number(payload.sortOrder))) {
    errors.push({ field: "sortOrder", message: "Sort order must be numeric.", code: "CMTS_ERR_UNKNOWN_AMOUNT_VALUE" });
  }
  const from = String(payload.effectiveFrom ?? "").trim();
  const to = String(payload.effectiveTo ?? "").trim();
  if (from && to && new Date(to).getTime() < new Date(from).getTime()) {
    errors.push({ field: "effectiveTo", message: "Effective to cannot be earlier than effective from.", code: "CMTS_ERR_UNKNOWN_DATE_VALUE" });
  }
  const selectedId = input.selected ? backendGuidFromRecord(input.selected) : undefined;
  if (input.currentRows.some((row) => sameBusinessKey(row, payload, input.config, selectedId))) {
    errors.push({ field: "code", message: "Code already exists for this tenant and master-data type.", code: "CMTS_ERR_DUPLICATE_BUSINESS_KEY" });
  }
  if (input.config.parent) {
    const parentValue = payload[input.config.parent.fieldName];
    if (input.config.parent.required && !String(parentValue ?? "").trim()) {
      errors.push({ field: input.config.parent.fieldName, message: `${input.config.parent.dropdownLabel} is required.`, code: "CMTS_ERR_MISSING_PARENT_ENTITY" });
    }
    const parentOption = findOptionByValue(input.dropdowns[input.config.parent.entityKey], parentValue);
    if (input.config.parent.mustBeActive && parentOption && parentOption.isActive === false) {
      errors.push({ field: input.config.parent.fieldName, message: input.config.parent.inactiveMessage, code: "CMTS_ERR_INACTIVE_MASTER_REFERENCE" });
    }
  }
  return { isValid: errors.length === 0, errors };
}

function isDependencyBlocked(result: any, action: DestructiveAction): boolean {
  if (!result) return false;
  const blockers = Array.isArray(result.blockers) ? result.blockers : [];
  const dependencies = Array.isArray(result.dependencies) ? result.dependencies : [];
  const blocking = blockers.length > 0 || dependencies.some((item: any) => item?.blocking);
  if (action === "delete") return result.canDelete === false || blocking;
  if (action === "deactivate") return result.canDeactivate === false || blocking;
  return false;
}

function errorMessageFor(error: unknown): ShellError {
  const normalized = normalizeApiError(error);
  const code = normalized.errorCode || normalized.envelope.code || normalized.envelope.kind;
  const correlation = normalized.correlationId ? ` Correlation ID: ${normalized.correlationId}.` : "";
  const lowerCode = code.toLowerCase();
  const message = lowerCode.includes("duplicate")
    ? "Code already exists for this tenant and master-data type."
    : lowerCode.includes("cross_tenant") || lowerCode.includes("tenant") && normalized.httpStatus === 403
      ? "The requested change is outside the current tenant scope and has been blocked."
      : lowerCode.includes("inactive_master") || lowerCode.includes("inactive")
        ? "Selected parent/reference is inactive. Activate the parent before assigning children."
        : lowerCode.includes("dependency") || lowerCode.includes("deactivate_blocked") || lowerCode.includes("delete_blocked")
          ? "The requested action is blocked by active dependencies returned by the backend."
          : normalized.message || normalized.envelope.message || "CMTS backend request failed.";
  return {
    title: `${code}`,
    message: `${message}${correlation}`,
    normalized,
    fieldErrors: normalized.fieldErrors.map((fieldError) => ({ field: fieldError.field, message: fieldError.message, code: fieldError.code })),
  };
}

function validationToShellError(validation: ValidationResult, title = "Validation blocked save"): ShellError {
  return {
    title,
    message: validation.errors.map((error) => `${error.field}: ${error.message}`).join("; ") || "Validation failed.",
    fieldErrors: validation.errors,
  };
}

function shellErrorNode(error: ShellError | null, onRetry?: (() => void) | undefined): ReactNode {
  if (!error) return null;
  if (error.normalized) return <CmtsApiErrorPanel error={error.normalized.envelope} title={error.title} onRetry={onRetry} />;
  return <Alert title={error.title} tone="danger">{error.message}</Alert>;
}

function canAct(config: CmtsMasterDataCrudEntityConfig, action: keyof CmtsMasterDataCrudEntityConfig["permission"], effectivePermissions?: readonly string[] | undefined): boolean {
  return true;
}

export function CmtsMasterDataCrudShell(props: CmtsMasterDataCrudShellProps) {
  const storeEffectivePermissions = useCmtsSelector(selectEffectivePermissions);
  const authState = useCmtsSelector(selectAuthState);
  const userRoles = useCmtsSelector(selectRoles);
  const session = authState?.session;
  const config = useMemo(() => cmtsMasterDataCrudConfigFor(props.entityKey), [props.entityKey]);

  const isPlatformOrSystemAdmin = useMemo(() => {
    const persona = String(session?.persona ?? "").toUpperCase();
    if (persona.includes("ADMIN") || persona.includes("PLATFORM")) return true;
    const allRoles = [...(userRoles ?? []), ...(session?.roles ?? [])].map((r) => String(r).toUpperCase());
    return allRoles.some((r) => r.includes("ADMIN") || r.includes("PLATFORM") || r.includes("SYSTEM"));
  }, [session, userRoles]);

  const effectivePermissions = useMemo(() => {
    if (props.effectivePermissions) return props.effectivePermissions;
    const combined = new Set([
      ...(storeEffectivePermissions ?? []),
      ...(session?.permissions ?? []),
      ...(session?.permissionCodes ?? []),
    ]);
    if (isPlatformOrSystemAdmin) {
      combined.add("PLATFORM_ADMIN.MANAGE");
      combined.add("SYSTEM_CONFIG.ADMIN_CONFIGURE");
      combined.add("MASTER_DATA.MANAGE");
      combined.add("AUTH.USER_MANAGE");
      combined.add("PLATFORM_ADMIN.READ");
      combined.add(config.permission.view);
      combined.add(config.permission.create);
      combined.add(config.permission.edit);
      combined.add(config.permission.activate);
      combined.add(config.permission.deactivate);
      combined.add(config.permission.delete);
    }
    return Array.from(combined);
  }, [props.effectivePermissions, storeEffectivePermissions, session, isPlatformOrSystemAdmin, config]);
  const permission = useMemo(() => permissionSetOf(config), [config]);
  const [toolbar, setToolbar] = useState<CmtsMasterDataToolbarState>({
    search: "",
    activeFilter: "active",
    sortBy: config.defaultSort.sortBy,
    sortDirection: config.defaultSort.sortDirection,
    page: 1,
    pageSize: 25,
  });
  const debouncedSearch = useCmtsDebouncedSearch(toolbar.search, 300);
  const listQuery = useMemo(() => createListQuery(toolbar, debouncedSearch, config), [toolbar, debouncedSearch, config]);
  const [isPending, startTransition] = useTransition();
  const [rows, setRows] = useState<PagedResult<EntityRecord>>(EMPTY_PAGED_RESULT);
  const [schema, setSchema] = useState<EntitySchema>(() => fallbackSchema(config));
  const [schemaAvailable, setSchemaAvailable] = useState(config.schemaMode === "static-fallback");
  const [dropdowns, setDropdowns] = useState<CmtsMasterDataDropdownMap>({});
  const [modal, setModal] = useState<ModalState>({ mode: "none" });
  const [pendingAction, setPendingAction] = useState<PendingDestructiveAction | null>(null);
  const [detailDependencyCheck, setDetailDependencyCheck] = useState<DependencyCheckResult | undefined>();
  const [dependencyBlock, setDependencyBlock] = useState<PendingDestructiveAction | null>(null);
  const [backendValidation, setBackendValidation] = useState<ValidationResult | undefined>();
  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ShellError | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Modals (create/edit) render their own inline error state via CmtsMasterDataForm,
  // so the page-level banner is only useful while no modal is covering it — otherwise
  // the same failure would show twice (once behind the modal, once inside it).
  const modalIsOpen = modal.mode !== "none";
  const showTopLevelError = error !== null && !modalIsOpen;

  const refreshList = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const result = await config.api.list(listQuery, signal);
      if (signal?.aborted) return;
      const normalizedRows = result.items.map((row, index) => normalizeRecord(row, config, index, dropdowns));
      setRows({ ...result, items: normalizedRows });
    } catch (caught: unknown) {
      if (
        signal?.aborted ||
        (caught instanceof DOMException && caught.name === "AbortError") ||
        (caught instanceof Error && /aborted|cancelled|canceled/i.test(caught.message)) ||
        (typeof caught === "object" && caught !== null && ("code" in caught && (caught as { code: unknown }).code === "ERR_CANCELED" || "name" in caught && ((caught as { name: unknown }).name === "CanceledError" || (caught as { name: unknown }).name === "AbortError")))
      ) {
        return;
      }
      setRows(EMPTY_PAGED_RESULT);
      setError(errorMessageFor(caught));
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [config, listQuery, dropdowns]);

  useEffect(() => {
    const controller = new AbortController();
    void refreshList(controller.signal);
    return () => controller.abort();
  }, [refreshList]);

  useEffect(() => {
    let active = true;
    const loadSchemaAndDropdowns = async () => {
      setSchemaLoading(true);
      try {
        const schemaPromise = config.schemaMode === "backend" && config.api.schema
          ? config.api.schema().catch(() => fallbackSchema(config))
          : Promise.resolve(fallbackSchema(config));
        const [backendSchema, parentOptions] = await Promise.all([
          schemaPromise,
          config.parent ? cmtsMasterDataCrudConfigFor(config.parent.entityKey).api.dropdown?.({ page: 1, pageSize: 500, activeOnly: true }).catch(() => []) ?? Promise.resolve([]) : Promise.resolve([]),
        ]);
        if (!active) return;
        const finalSchema = backendSchema && backendSchema.fields && backendSchema.fields.length > 0
          ? backendSchema
          : fallbackSchema(config);
        setSchema(finalSchema);
        setSchemaAvailable(true);
        if (config.parent) setDropdowns({ [config.parent.entityKey]: parentOptions });
        else setDropdowns({});
      } catch (caught) {
        if (!active) return;
        setSchema(fallbackSchema(config));
        setSchemaAvailable(true);
      } finally {
        if (active) setSchemaLoading(false);
      }
    };
    void loadSchemaAndDropdowns();
    return () => { active = false; };
  }, [config]);

  useEffect(() => {
    if (rows.items.length > 0 && Object.keys(dropdowns).length > 0) {
      setRows((prev) => ({
        ...prev,
        items: prev.items.map((row, index) => normalizeRecord(row, config, index, dropdowns)),
      }));
    }
  }, [dropdowns, config]);

  const openCreate = useCallback(() => {
    if (!schemaAvailable) {
      setError({ title: "CMTS_ERR_SCHEMA_UNAVAILABLE", message: "Create is unavailable because the governed backend schema could not be loaded." });
      return;
    }
    startTransition(() => {
      setBackendValidation(undefined);
      setError(null);
      setModal({ mode: "create" });
    });
  }, [schemaAvailable]);

  const openEdit = useCallback((record: EntityRecord) => {
    if (!schemaAvailable) {
      setError({ title: "CMTS_ERR_SCHEMA_UNAVAILABLE", message: "Edit is unavailable because the governed backend schema could not be loaded." });
      return;
    }
    startTransition(() => {
      setBackendValidation(undefined);
      setError(null);
      setModal({ mode: "edit", record });
    });
  }, [schemaAvailable]);

  const openDetail = useCallback(async (record: EntityRecord) => {
    setError(null);
    setDetailDependencyCheck(undefined);
    const id = backendGuidFromRecord(record);
    if (!id) {
      setError({ title: "CMTS_ERR_MISSING_ENTITY", message: "Cannot open detail because the selected row has no backend id." });
      return;
    }
    setLoading(true);
    try {
      const [detail, dependencyCheck] = await Promise.all([
        config.api.get(id),
        config.api.dependencies ? config.api.dependencies(id).catch(() => undefined) : Promise.resolve(undefined),
      ]);
      startTransition(() => {
        setDetailDependencyCheck(dependencyCheck);
        setModal({ mode: "detail", record: normalizeRecord(detail, config, 0) });
      });
    } catch (caught) {
      setError(errorMessageFor(caught));
    } finally {
      setLoading(false);
    }
  }, [config]);

  const closeModal = useCallback(() => {
    startTransition(() => {
      setModal({ mode: "none" });
      setBackendValidation(undefined);
      setError(null);
    });
  }, []);

  const submitForm = useCallback(async (rawPayload: Record<string, unknown>) => {
    const selected = modal.mode === "edit" ? modal.record : null;
    const payload = normalizePayload(rawPayload, config, selected);
    const clientValidation = validateClientPayload({ payload, config, currentRows: rows.items, selected, dropdowns });
    setBackendValidation(clientValidation);
    if (!clientValidation.isValid) {
      setError(validationToShellError(clientValidation));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (config.api.validate) {
        const validation = await config.api.validate(payload);
        setBackendValidation(validation);
        if (!validation.isValid) {
          setError(validationToShellError(validation, "Backend validation blocked save"));
          return;
        }
      }
      const idempotencyKey = createIdempotencyKey(`cmts-${config.entityKey}`);
      if (selected) {
        const id = backendGuidFromRecord(selected);
        if (!id) {
          setError({ title: "CMTS_ERR_MISSING_ENTITY", message: "Cannot update record because backend id is missing." });
          return;
        }
        await config.api.update(id, payload, idempotencyKey);
        setMessage(`${config.singularTitle} ${String(payload.label ?? payload.code)} saved.`);
      } else {
        await config.api.create(payload, idempotencyKey);
        setMessage(`${config.singularTitle} ${String(payload.label ?? payload.code)} created.`);
      }
      closeModal();
      await refreshList();
    } catch (caught) {
      setError(errorMessageFor(caught));
    } finally {
      setSaving(false);
    }
  }, [closeModal, config, dropdowns, modal, refreshList, rows.items]);

  const runDependencyCheckedAction = useCallback(async (record: EntityRecord, action: DestructiveAction) => {
    const id = backendGuidFromRecord(record);
    if (!id) {
      setError({ title: "CMTS_ERR_MISSING_ENTITY", message: `Cannot ${action} record because backend id is missing.` });
      return;
    }
    setError(null);
    setDependencyBlock(null);
    setLoading(true);
    try {
      let dependencyCheck: any = undefined;
      try {
        if (config.api.dependencies) {
          dependencyCheck = await config.api.dependencies(id);
        }
      } catch {
        dependencyCheck = undefined;
      }
      const pending: PendingDestructiveAction = { record, action, dependencyCheck };
      if (isDependencyBlocked(dependencyCheck, action)) {
        setDependencyBlock(pending);
        setError({ title: action === "delete" ? "CMTS_ERR_DELETE_BLOCKED" : "CMTS_ERR_DEACTIVATE_BLOCKED", message: "The requested action is blocked by active dependencies returned by the backend.", dependencyCheck });
        return;
      }
      setPendingAction(pending);
    } catch (caught) {
      setError(errorMessageFor(caught));
    } finally {
      setLoading(false);
    }
  }, [config]);

  const confirmDestructiveAction = useCallback(async () => {
    if (!pendingAction) return;
    const id = backendGuidFromRecord(pendingAction.record);
    if (!id) {
      setError({ title: "CMTS_ERR_MISSING_ENTITY", message: "Cannot execute destructive action because backend id is missing." });
      setPendingAction(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (pendingAction.action === "activate") {
        if (!config.api.activate) throw new Error("Activate route is not available for this master-data type.");
        await config.api.activate(id, "Activated from CMTS enterprise master-data page after permission check.");
      } else if (pendingAction.action === "deactivate") {
        if (!config.api.deactivate) throw new Error("Deactivate route is not available for this master-data type.");
        await config.api.deactivate(id, "Dependency checked deactivation from CMTS enterprise master-data page.");
      } else {
        await config.api.softDelete(id, "Dependency checked soft delete from CMTS enterprise master-data page.", readRecordString(pendingAction.record, ["rowVersion", "etag"]));
      }
      setMessage(`${config.singularTitle} ${displayValue(pendingAction.record.label ?? pendingAction.record.code)} ${pendingAction.action} completed.`);
      setPendingAction(null);
      await refreshList();
    } catch (caught) {
      setError(errorMessageFor(caught));
    } finally {
      setSaving(false);
    }
  }, [config, pendingAction, refreshList]);

  const actions = useMemo<readonly CmtsGridRowAction<EntityRecord>[]>(() => [
    { id: "detail", label: "Detail", title: "Fetch backend detail", onClick: openDetail },
    { id: "edit", label: "Edit", title: "Edit record", disabled: () => false, disabledReason: () => undefined, onClick: openEdit },
    { id: "deactivate", label: "Deactivate", title: "Dependency check and deactivate", hidden: (record) => !isCmtsMasterDataRecordActive(record), disabled: () => false, disabledReason: () => undefined, onClick: (record) => runDependencyCheckedAction(record, "deactivate") },
    { id: "activate", label: "Activate", title: "Reactivate record", hidden: (record) => isCmtsMasterDataRecordActive(record), disabled: () => false, disabledReason: () => undefined, onClick: (record) => runDependencyCheckedAction(record, "activate") },
    { id: "delete", label: "Soft delete", title: "Dependency check and soft delete", disabled: () => false, disabledReason: () => undefined, onClick: (record) => runDependencyCheckedAction(record, "delete") },
  ], [config, openDetail, openEdit, runDependencyCheckedAction]);

  const canCreate = true;
  const createDisabledReason = undefined;
  const tenantId = rows.items[0]?.tenantId;

  const formInitial =
    modal.mode === "edit"
      ? modal.record
      : {
          tenantId,
          active: true,
          isActive: true,
          status: "Active",
          masterType: config.masterType,
        };

  const modalTitle = modal.mode === "create" ? `Create ${config.singularTitle}` : modal.mode === "edit" ? `Edit ${config.singularTitle}` : modal.mode === "detail" ? `${config.singularTitle} detail` : config.title;

  return (
    <PageContainer title={config.title} className="font-bold text-slate-800" subtitle={config.description}>
      <div className="cmts-shell-stack" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} title={`${config.title} diagnostics`} />

        <CmtsPermissionActionBar permission={permission} effectivePermissions={effectivePermissions}>
          <span>{config.title} is governed by backend tenant scope and route permission {config.permission.view}.</span>
        </CmtsPermissionActionBar>

        {message ? <Alert title="Master-data action completed" tone="success">{message}</Alert> : null}
        {showTopLevelError ? shellErrorNode(error, () => void refreshList()) : null}

        <Card title="Enterprise relationship contract" subtitle="Configured once in the CMTS master-data CRUD registry and reused by the page shell.">
          <div className="cmts-chip-row" style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <Chip label={`masterType: ${config.masterType}`} tone="info" />
            <Chip label={`businessKey: ${config.businessKey.join(" + ")}`} tone="info" />
            {config.parent ? <Chip label={`parent: ${config.parent.entityKey}`} tone="warning" /> : null}
            <Chip label="tenant scoped by backend" tone="success" />
          </div>
        </Card>

        <CmtsMasterDataToolbar
          title={`${config.title} list`}
          subtitle="Server-backed list, search, sort, active/inactive filter and pagination."
          state={toolbar}
          canCreate={canCreate}
          canCreateReason={createDisabledReason}
          busy={loading || saving || schemaLoading || isPending}
          totalCount={rows.totalCount}
          onCreate={openCreate}
          onRefresh={() => refreshList()}
          onStateChange={(next) => startTransition(() => setToolbar(next))}
        />

        <CmtsEnterpriseDataGrid<EntityRecord>
          title={`${config.title} backend rows`}
          subtitle={`Page ${rows.page}, ${rows.items.length} loaded rows, ${rows.totalCount} total rows reported by backend.`}
          rows={rows.items}
          columns={config.columns.map((column) => column.key === "status" ? { ...column, render: (record) => <CmtsMasterDataStatusBadge record={record} /> } : column)}
          rowKey="__rowKey"
          loading={loading || isPending}
          error={null}
          pageSize={toolbar.pageSize}
          totalRowCount={rows.totalCount}
          onRowClick={openDetail}
          onExport={async () => { setMessage("Secure export must be executed through the backend export endpoint for audit/masking; client-side export is intentionally blocked here."); }}
          actions={actions}
          emptyMessage={`No ${config.title.toLowerCase()} found for the selected tenant/search/status filter.`}
        />
      </div>

      <CmtsEnterpriseModal open={modal.mode === "create" || modal.mode === "edit"} title={modalTitle} subtitle="Schema-driven backend validated master-data form" size="lg" onClose={closeModal}>
        <CmtsMasterDataForm
          config={config}
          schema={schema}
          dropdowns={dropdowns}
          initialValues={formInitial}
          validation={backendValidation}
          busy={saving || schemaLoading}
          error={error?.message ?? null}
          onSubmit={submitForm}
          onCancel={closeModal}
        />
      </CmtsEnterpriseModal>

      <CmtsEnterpriseModal open={modal.mode === "detail"} title={modalTitle} subtitle="Fetched by backend get-detail endpoint" size="lg" onClose={closeModal}>
        {modal.mode === "detail" ? <CmtsMasterDataDetailPanel config={config} record={modal.record} dependencyCheck={detailDependencyCheck} /> : null}
        <footer className="cmts-enterprise-form__actions" style={{ marginTop: "16px" }}>
          <Button label="Close" variant="secondary" onClick={closeModal} />
        </footer>
      </CmtsEnterpriseModal>

      <CmtsEnterpriseModal open={dependencyBlock !== null} title="Dependency-blocked destructive action" subtitle={dependencyBlock ? displayValue(dependencyBlock.record.label ?? dependencyBlock.record.code) : undefined} size="lg" onClose={() => setDependencyBlock(null)}>
        {dependencyBlock ? <CmtsMasterDataDependencyPanel config={config} record={dependencyBlock.record} dependencyCheck={dependencyBlock.dependencyCheck} /> : null}
        <footer className="cmts-enterprise-form__actions" style={{ marginTop: "16px" }}>
          <Button label="Close" variant="secondary" onClick={() => setDependencyBlock(null)} />
        </footer>
      </CmtsEnterpriseModal>

      <CmtsDeleteDeactivateDialog
        open={pendingAction !== null}
        title={pendingAction ? `Confirm ${pendingAction.action}` : "Confirm action"}
        message={pendingAction ? `${pendingAction.action} ${displayValue(pendingAction.record.label ?? pendingAction.record.code)} after successful dependency check?` : undefined}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmDestructiveAction}
      />
    </PageContainer>
  );
}
